
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { CharacterClass, Helix2000Character } from '../../shared/CharacterTypes';
import { createHelix2000Character } from '../../shared/CharacterClasses';
import { applyDevUrlOverrides } from './devUrlOverrides';
import { CharacterService, DBCharacter } from '../services/CharacterService';
import { storage } from '../services/StorageService';
import { DevConfig } from '../../config/environment/devConfig';
import { isMainGameDomain } from '../../config/environment/environment';
import { gameLogger } from '../../utils/GameLogger';
import { issueDevToken } from '../services/devAuthService';
import { isGuestJwt, isJwtExpired, readDevJwtCharacterClass } from '../services/authToken';
import { useGame } from '../ui/contexts/GameContext';
import { bootSearchParams } from '../../config/boot/bootUrl';

export interface TwitchSessionInfo {
  twitchUserId: string;
  twitchLogin: string;
  twitchDisplayName: string;
  email: string | null;
}

let guestBootChecked = false;
/** Tests only — the guest boot check runs once per page. */
export function _testOnly_resetGuestBootCheck(): void { guestBootChecked = false; }

function readUsableCachedJwt(expectedDevClass?: CharacterClass | null): string | null {
  const firstReadThisPage = !guestBootChecked;
  guestBootChecked = true;
  let token: string | null = null;
  try { token = storage.get('helix_jwt'); } catch { return null; }

  if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
    if (token !== null) {
      gameLogger.warn('game', '🧹 Cleaning up malformed token from storage');
      storage.remove('helix_jwt');
    }
    return null;
  }

  if (isJwtExpired(token)) {
    gameLogger.warn('AUTH', '⌛ Cached session token expired; removing it before boot');
    storage.remove('helix_jwt');
    return null;
  }

  // A guest's hero is session-only (D27). Its portal and reload navigations
  // carry keepCharacter=1 and keep the guest token; a page that boots without
  // it starts signed out, offering sign-in or a fresh guest, not the roster.
  // Checked once per page: a guest token issued on this page is kept.
  if (firstReadThisPage && isGuestJwt(token) && bootSearchParams().get('keepCharacter') !== '1') {
    gameLogger.info('AUTH', '👤 Ending the cached guest session on a new visit');
    storage.remove('helix_jwt');
    storage.remove('helix_last_character');
    return null;
  }

  // A strict-localhost synthetic boot persists memory state by the dev JWT's
  // userId. Reusing a warrior-issued token after reloading with `?class=mage`
  // makes the server correctly render a Mage shell but rehydrate the warrior
  // hotbar and trained set from that old identity. Rotate only a verified-shape
  // dev token whose issuance-time class disagrees with the requested class;
  // opaque and non-dev tokens stay under server authority.
  if (expectedDevClass) {
    const cachedDevClass = readDevJwtCharacterClass(token);
    if (cachedDevClass !== undefined && cachedDevClass !== expectedDevClass) {
      gameLogger.info(
        'AUTH',
        `Rotating cached dev token for class change: ${cachedDevClass ?? 'none'} → ${expectedDevClass}`,
      );
      storage.remove('helix_jwt');
      return null;
    }
  }

  return token;
}

export function useAuth() {
  // Twitch OAuth callback breadcrumb (?twitch=ok / ?twitch=error). Suppresses
  // dev-auto-spawn on post-OAuth landings.
  const hasTwitchCallbackBreadcrumb = (() => {
    try {
      const v = bootSearchParams().get('twitch');
      return v === 'ok' || v === 'error';
    } catch { return false; }
  })();

  // Repeat-visit hint: set after a successful /api/auth/twitch/session probe
  // (see useEffect below), cleared on twitchLogout or when a probe reports no
  // authenticated session. Lets us synchronously suppress the dev-auto-spawn
  // for Twitch users opening the app in a new tab — without this, every page
  // load would briefly flash the dev character before the async probe wins. If
  // the hint is stale (server lost the cookie), the probe reports signed-out
  // and we re-apply the dev shortcut belatedly (see runDevShortcut).
  const hasTwitchRecentHint = (() => {
    try { return storage.get('helix_twitch_recent') === '1'; } catch { return false; }
  })();

  // Defer the dev shortcut whenever there's any signal a Twitch session may
  // be in play. The dev loop (no Twitch ever signed in) keeps the instant
  // synchronous spawn — zero behavior change for DJ's daily flow.
  const shouldDeferForTwitch = hasTwitchCallbackBreadcrumb || hasTwitchRecentHint;
  // URL/mode skipLogin controls presentation, not public authentication.
  // The canonical apex/www surfaces always probe the server-owned Twitch
  // cookie; local/PTR shortcuts retain their existing server-gated behavior.
  const allowDevShortcut = DevConfig.skipLogin && !isMainGameDomain();
  const initialSkipLogin = allowDevShortcut && !shouldDeferForTwitch;

  // Local dev network signal (localhost, Tailscale 100.x, or local LAN 192.168.x).
  const isStrictLocalhost = (() => {
    try {
      const h = (globalThis as any).window?.location?.hostname;
      return h === 'localhost' || h === '127.0.0.1' || h === '100.97.49.43' || h?.startsWith('192.168.') || h?.startsWith('100.');
    } catch { return false; }
  })();

  // A synthetic `devplayer_*` character can only ever JOIN a room in two
  // situations: (a) strict-localhost memory-mode, where the server
  // synthesizes an ephemeral character from the dev-token claims
  // (PlayerLifecycleService gates that on !isDbConnected() + dev token), or
  // (b) a singleplayer surface (systems.multiplayer=false) where no room
  // join ever happens. On a Mongo-backed multiplayer surface (PTR/tunnels)
  // the join handler does CharacterModel.findOne(characterId) — a synthetic
  // id is never found, the server kicks NO_CHARACTER, and the player stands
  // in a rendered but unconnected world. That was the class-switch stall
  // (2026-06-12): a cached helix_jwt made isAuthenticated true on frame 1,
  // this synthetic player satisfied Game.tsx's `!player` gate, PreGameFlow
  // never mounted, and the ?class auto-pick (resolveUrlClassCharacter) — the only
  // path that creates a REAL character — never ran. Returning null here
  // routes those surfaces through PreGameFlow instead.
  const canAutoSpawnSynthetic = isStrictLocalhost || !DevConfig.systems.multiplayer;

  // Show the character-select page even when skipLogin=1 is auto-authing.
  // We still produce a DEV_TOKEN (so PreGameFlow jumps straight to
  // character_select without auth/greeter/server screens) but leave `player`
  // null so Game.tsx enters PreGameFlow instead of GameScene.
  //
  // Default flipped to TRUE on PTR + QA tunnels (2026-04-26) — DJ wants the
  // bare URL to land on the character roster instead of auto-spawning.
  // EXCEPTION (2026-06-04): on a strict-localhost surface the default flips
  // back to FALSE, so `make dev-fast-memory` + bare localhost:3001 lands
  // STRAIGHT in-world with a deterministic DevHero (devCharacterClass /
  // devCharacterName) — zero clicks, no Mongo, no picker. This is the
  // local-only dev-into-world fast path; PTR/tunnels still get the picker
  // because they are never `localhost`. Opt back to the picker locally via
  // `?pickCharacter=1`; opt OUT of auto-spawn on a tunnel via
  // `?pickCharacter=0`. Originally shipped 2026-04-20 as opt-IN for
  // `make tunnel-char-select`.
  const pickCharacter = (() => {
    try {
      const params = bootSearchParams();
      // Full-page portal/item transfers carry keepCharacter=1 so PreGameFlow
      // can recover the persisted `helix_last_character`. On strict localhost
      // the normal default is a synthetic memory-mode hero; allowing that
      // shortcut here bypasses the bridge and database-backed servers reject
      // the synthetic id, leaving a black world with disconnected HUD chrome.
      if (params.get('keepCharacter') === '1') return true;
      const raw = params.get('pickCharacter');
      if (raw !== null) return raw !== '0';          // explicit URL override wins
      return !isStrictLocalhost;                       // local → auto-spawn; else → picker
    } catch { return !isStrictLocalhost; }
  })();

  // Honor `?class=X` URL param when auto-spawning. Used by the in-game
  // ClassSwitcherTopBar (added 2026-04-26) so clicking a class on the bar
  // reloads the page with the new class baked into the URL. Falls back to
  // DevConfig.devCharacterClass when omitted or invalid.
  const urlClassOverride = (() => {
    try {
      const raw = bootSearchParams().get('class');
      if (!raw) return null;
      const lower = raw.toLowerCase();
      const valid = (Object.values(CharacterClass) as string[]).includes(lower);
      return valid ? (lower as CharacterClass) : null;
    } catch { return null; }
  })();

  const expectedSyntheticDevClass = initialSkipLogin && !pickCharacter && canAutoSpawnSynthetic
    ? urlClassOverride
    : null;

  const getInitialPlayer = (): Helix2000Character | null => {
    if (!initialSkipLogin) return null;
    if (pickCharacter) return null;
    if (!canAutoSpawnSynthetic) return null;
    try {
      const charClass = urlClassOverride
        ?? (CharacterClass[(DevConfig.devCharacterClass as string).toUpperCase() as keyof typeof CharacterClass] || CharacterClass.WARRIOR);
      const player = createHelix2000Character(DevConfig.devCharacterName || 'DevHero', charClass);

      // Force a unique ID for every tab load to prevent multi-tab ghost collisions.
      const uniqueSuffix = `${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      const finalId = `devplayer_${uniqueSuffix}`;
      player._id = finalId;
      player.id = finalId;
      
      // Ensure the name is unique as well to bypass the server's isSameDevPlayer check
      if (!DevConfig.devExactName) player.name = `${player.name}_${Math.floor(Math.random() * 1000)}`;

      applyDevUrlOverrides(player);

      gameLogger.info('SYSTEM', '✨ Created unique dev character for tab:', finalId);

      return player;
    } catch (err) {
      gameLogger.error('game', 'Failed to create initial dev character:', { error: err });
      return null;
    }
  };

  /** Persist a synthetic dev character into `helix_last_character` so the
   *  `keepCharacter=1` portal-navigation bridge in PreGameFlow can recover it.
   *  Only the fields `convertDBToGameCharacter` reads are required. */
  const persistDevCharacterForBridge = (char: Helix2000Character): void => {
    try {
      storage.setObject('helix_last_character', {
        _id: char._id,
        name: char.name,
        class: char.class,
        level: char.level,
        xp: char.xp,
        x: char.position.x,
        y: char.position.y,
        health: char.health,
        maxHealth: char.maxHealth,
        mana: char.mana,
        maxMana: char.maxMana,
      });
    } catch { /* best-effort storage */ }
  };

  // Phase 3 (gap-closing) — replaced the literal 'DEV_TOKEN' initial
  // value with a sync read from localStorage (a previously-issued dev
  // JWT, valid for 24h). When storage is empty + initialSkipLogin is
  // true, the useEffect below fires an async /api/auth/dev/issue
  // call to mint a fresh JWT. UX: previous-visit users see no flash
  // (storage hit), first-time users see a one-tick null → JWT
  // transition.
  const cachedJwt = readUsableCachedJwt(expectedSyntheticDevClass);
  const [authToken, setAuthToken] = useState<string | null>(
    initialSkipLogin ? (cachedJwt || null) : null
  );
  const [isAuthenticated, setIsAuthenticated] = useState(
    initialSkipLogin && !!cachedJwt
  );

  // Phase 1 of the Twitch auth migration arc — server-side session lookup.
  // Lives in an HttpOnly cookie set by /api/auth/twitch/callback. We read
  // it once on mount and pass through to consumers (PreGameFlow, AuthScreen,
  // future Phase 2 Colyseus onAuth integration). Phase 1 does NOT yet flip
  // isAuthenticated from this — character binding is a Phase 3 concern.
  const [twitchSession, setTwitchSession] = useState<TwitchSessionInfo | null>(null);
  const [twitchProbed, setTwitchProbed] = useState(false);

  useEffect(() => {
    // Preserve the hint-free local/PTR shortcut. Public main-game surfaces
    // always probe cookies, even when their mode injects skipLogin=1.
    if (initialSkipLogin) {
      setTwitchProbed(true);
      return;
    }
    let cancelled = false;
    fetch('/api/auth/twitch/session', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (data?.authenticated && data?.sessionToken) {
          // Twitch session wins — promote authToken + isAuthenticated so
          // PreGameFlow routes to character_select. Same JWT lives in both
          // the HttpOnly cookie + helix_jwt localStorage so the existing
          // ApiClient Bearer-header flow works unchanged.
          //
          // setPlayer(null) is the load-bearing line: on a "new tab, no
          // hint" first-visit path, the synchronous dev shortcut already
          // spawned a player and Game.tsx is rendering the game scene.
          // Clearing the player demotes Game.tsx back into PreGameFlow,
          // which then routes to character_select via the new initialToken.
          // User sees a brief dev-char flash, then the splash, then
          // character_select. Subsequent visits in the same browser have
          // helix_twitch_recent set and skip this dance entirely.
          setTwitchSession({
            twitchUserId: data.twitchUserId,
            twitchLogin: data.twitchLogin,
            twitchDisplayName: data.twitchDisplayName,
            email: data.email ?? null,
          });
          setAuthToken(data.sessionToken);
          setIsAuthenticated(true);
          setPlayer(null);
          storage.set('helix_jwt', data.sessionToken);
          storage.set('helix_twitch_recent', '1');
          gameLogger.info('AUTH', `🟣 Twitch session active: ${data.twitchLogin} → routing to character_select`);
        } else {
          // Probe reported no authenticated session. Clear the stale hint.
          if (hasTwitchRecentHint) {
            try { storage.remove('helix_twitch_recent'); } catch { /* best-effort storage */ }
            // We suppressed the dev shortcut earlier on the basis of a stale
            // hint — apply it belatedly so DJ doesn't get stranded on the
            // splash. If we're not in a dev-skipLogin context, this is a no-op
            // and the splash stays visible (correct behavior).
            runDevShortcut();
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (hasTwitchRecentHint) {
          try { storage.remove('helix_twitch_recent'); } catch { /* best-effort storage */ }
          runDevShortcut();
        }
      })
      .finally(() => { if (!cancelled) setTwitchProbed(true); });

    function runDevShortcut() {
      if (cancelled) return;
      // Never turn a stale hint into dev issuance on the public main domain.
      if (!allowDevShortcut) return;
      // Phase 3 (gap-closing) — async /api/auth/dev/issue call. The
      // legacy synchronous 'DEV_TOKEN' literal is gone. Endpoint returns
      // 410 Gone on staging/prod (env-class=dev only), in which case
      // issueDevToken returns null and we leave the user at the splash.
      // Same synthetic-character gate as getInitialPlayer — a devplayer_*
      // id can't join a Mongo-backed multiplayer room, so only build/set it
      // where it's actually usable. The token itself is still minted either
      // way (PreGameFlow needs it to reach character_select).
      const devPlayer = canAutoSpawnSynthetic ? getInitialPlayerForDevShortcut() : null;
      issueDevToken({
        characterClass: urlClassOverride || DevConfig.devCharacterClass,
        characterName: DevConfig.devCharacterName,
      }).then((result: { token: string } | null) => {
        if (cancelled || !result) return;
        setAuthToken(result.token);
        setIsAuthenticated(true);
        if (devPlayer && !pickCharacter) {
          setPlayer(devPlayer);
          persistDevCharacterForBridge(devPlayer);
        }
        try { storage.set('helix_jwt', result.token); } catch { /* best-effort storage */ }
      });
    }

    return () => { cancelled = true; };
    // Mount-only session probe; all mutable work is cancellation-guarded.
  }, []);

  function getInitialPlayerForDevShortcut(): Helix2000Character | null {
    try {
      const charClass = urlClassOverride
        ?? (CharacterClass[(DevConfig.devCharacterClass as string).toUpperCase() as keyof typeof CharacterClass] || CharacterClass.WARRIOR);
      const player = createHelix2000Character(DevConfig.devCharacterName || 'DevHero', charClass);
      const uniqueSuffix = `${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      const finalId = `devplayer_${uniqueSuffix}`;
      player._id = finalId; player.id = finalId;
      if (!DevConfig.devExactName) player.name = `${player.name}_${Math.floor(Math.random() * 1000)}`;
      applyDevUrlOverrides(player);
      gameLogger.info('SYSTEM', '✨ Created dev character (post-Twitch-stale fallback):', finalId);
      return player;
    } catch (err) {
      gameLogger.error('game', 'Failed to create fallback dev character:', { error: err });
      return null;
    }
  }
  // Player is owned by GameContext (SSOT, keystone 2026-06-05) so the unified
  // window engine's WindowHost-mounted gameplay windows can read
  // useGame().player. useAuth is the boot LOGIC that drives it — it no longer
  // holds its own useState copy. (Every setPlayer call site below is unchanged;
  // it now targets the context setter, which all downstream consumers already
  // receive via useAuth's return.)
  const { player, setPlayer } = useGame();

  // Synchronous (pre-paint) one-time hydration of the dev character. Replaces
  // the old `useState(() => getInitialPlayer())` lazy init. useLayoutEffect
  // runs after render but BEFORE the browser paints, so DJ's daily strict-
  // localhost flow still lands in-world on frame 1 with NO PreGameFlow flash,
  // and getInitialPlayer() runs exactly once (ref-guarded — no per-render
  // churn, the property the old lazy-init was protecting).
  const hydratedRef = useRef(false);
  useLayoutEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const initial = getInitialPlayer();
    if (initial) {
      setPlayer(initial);
      // Seed helix_last_character so the keepCharacter=1 bridge
      // (portal navigation via SceneEventManager.navigateToMap) can
      // recover this character on the destination page. Without this,
      // PreGameFlow.readLastCharacter() returns null and falls through
      // to minting a new mem_* roster character whose ID doesn't match
      // the transfer receipt → claimArrival rejects → BAD_TRANSFER 4007.
      persistDevCharacterForBridge(initial);
    }
    // Mount-only synchronous hydration guarded by hydratedRef.
  }, []);

  const characterService = useRef(CharacterService.getInstance());

  // Sync auth state if token exists on mount
  useEffect(() => {
    const token = readUsableCachedJwt(expectedSyntheticDevClass);
    const isValidToken = !!token && (!token.startsWith('local_') || initialSkipLogin);

    if (isValidToken && !isAuthenticated) {
      gameLogger.info('game', '🔑 Valid session token found, restoring session');
      setAuthToken(token);
      setIsAuthenticated(true);
    } else if (token && token.startsWith('local_') && !initialSkipLogin) {
      gameLogger.warn('game', '🚫 Ignoring mock local token to enforce real backend auth');
      storage.remove('helix_jwt');
    }
  }, [expectedSyntheticDevClass, initialSkipLogin, isAuthenticated]);

  // Handle Fast Boot Persistence — Phase 3 (gap-closing). The legacy
  // path stored the literal 'DEV_TOKEN' here; now we mint a real JWT
  // when storage is empty + we're in skipLogin mode. Cached JWT in
  // storage means subsequent loads skip the network call.
  useEffect(() => {
    if (!initialSkipLogin) return;
    const cachedToken = readUsableCachedJwt(expectedSyntheticDevClass);
    if (cachedToken) return;  // already have one — skip
    let cancelled = false;
    issueDevToken({
      characterClass: urlClassOverride || DevConfig.devCharacterClass,
      characterName: DevConfig.devCharacterName,
    }).then((result: { token: string } | null) => {
      if (cancelled || !result) return;
      // Don't overwrite a token that appeared while the network request was in
      // flight (e.g. a Playwright addInitScript or a Twitch session probe that
      // resolved between mount and the fetch response).
      const existing = readUsableCachedJwt(expectedSyntheticDevClass);
      if (existing) {
        setAuthToken(existing);
        setIsAuthenticated(true);
        return;
      }
      try { storage.set('helix_jwt', result.token); } catch { /* best-effort storage */ }
      setAuthToken(result.token);
      setIsAuthenticated(true);
    });
    return () => { cancelled = true; };
  }, [expectedSyntheticDevClass, initialSkipLogin]);

  const handleStartGame = (token: string, characterData: DBCharacter) => {
    try {
      setAuthToken(token);
      const gameChar = characterService.current.convertDBToGameCharacter(characterData);
      setPlayer(gameChar);
      setIsAuthenticated(true);
    } catch (error) {
      gameLogger.error('game', '❌ Failed to start game:', { error });
    }
  };

  const logout = () => {
    storage.remove('helix_jwt');
    setAuthToken(null);
    setIsAuthenticated(false);
    setPlayer(null);
  };

  const twitchLogout = async () => {
    try {
      await fetch('/api/auth/twitch/logout', { method: 'POST', credentials: 'same-origin' });
    } catch { /* best-effort */ }
    setTwitchSession(null);
    // Clear the synchronous-suppression hint so the next page load goes
    // straight to the dev shortcut (or splash) without waiting on a
    // guaranteed signed-out probe.
    try { storage.remove('helix_twitch_recent'); } catch { /* best-effort storage */ }
    // If the user was Twitch-authed, also clear authToken + isAuthenticated
    // so they bounce back to the splash. They can sign in again from there.
    // Phase 3 (gap-closing) — dropped the literal `!== 'DEV_TOKEN'` check;
    // dev tokens are now real JWTs and behave like any other auth token.
    if (authToken && !authToken.startsWith('local_')) {
      setAuthToken(null);
      setIsAuthenticated(false);
      try { storage.remove('helix_jwt'); } catch { /* best-effort storage */ }
    }
  };

  return {
    authToken,
    isAuthenticated,
    player,
    setPlayer,
    handleStartGame,
    logout,
    setIsAuthenticated,
    setAuthToken,
    twitchSession,
    twitchProbed,
    twitchLogout,
  };
}
